import { PlaidTransaction } from "../types/transactions";
import { isCheck, isZelle } from "./payment-type";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type SemanticType =
  | "CHECK"
  | "ZELLE"
  | "TRANSFER"
  | "ATM"
  | "DIRECT_DEBIT"
  | "PURCHASE"
  | "OTHER";

export type NormalizationSource =
  | "ENTITY_ID"
  | "ACH_PAYEE"
  | "ORIG_CO_NAME"
  | "INLINE_MERCHANT"
  | "ZELLE_COUNTERPARTY"
  | "CARD_PAYMENT"
  | "WIRE_PARTY"
  | "MERCHANT_NAME"
  | "NAME"
  | "DESCRIPTION";

export interface NormalizedMerchant {
  /** Lowercase stable key used for grouping */
  key: string;
  /** Title-cased name used for display */
  display: string;
  /** Which field/strategy produced this result */
  source: NormalizationSource;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const SELF_TRANSFER_PFC_DETAILED = new Set([
  "TRANSFER_IN_ACCOUNT_TRANSFER",
  "TRANSFER_OUT_ACCOUNT_TRANSFER",
  "TRANSFER_IN_SAVINGS_TRANSFERS",
  "TRANSFER_OUT_SAVINGS_TRANSFERS",
]);

const DIRECT_DEBIT_CODES = new Set(["direct debit", "bill payment", "standing order"]);

/**
 * All 50 US states + DC + US territories (2-letter USPS codes).
 * Used to deterministically identify the trailing state code in WF card statements.
 */
const US_STATE_CODES = new Set([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
  "PR",
  "VI",
  "GU",
  "AS",
  "MP",
]);

/**
 * Common 3-letter ISO 3166-1 alpha-3 country codes seen in WF INTL card statements.
 */
const ISO3_COUNTRY_CODES = new Set([
  "MEX",
  "CAN",
  "GBR",
  "DEU",
  "FRA",
  "ESP",
  "ITA",
  "NLD",
  "BRA",
  "ARG",
  "COL",
  "PER",
  "CHL",
  "ECU",
  "VEN",
  "PAN",
  "CRI",
  "GTM",
  "HND",
  "SLV",
  "NIC",
  "DOM",
  "CUB",
  "JAM",
  "TTO",
  "IRL",
  "AUS",
  "NZL",
  "JPN",
  "CHN",
  "KOR",
  "IND",
  "SGP",
  "HKG",
  "ARE",
  "ISR",
  "ZAF",
  "NGA",
  "KEN",
  "EGY",
  "MAR",
  "TUN",
  "GHA",
  "SWE",
  "NOR",
  "DNK",
  "FIN",
  "BEL",
  "CHE",
  "AUT",
  "PRT",
  "POL",
  "CZE",
  "HUN",
  "ROU",
  "BGR",
  "GRC",
  "TUR",
  "UKR",
  "RUS",
  "SAU",
  "KWT",
  "QAT",
  "BHR",
  "OMN",
  "JOR",
  "LBN",
  "PAK",
  "BGD",
  "LKA",
  "MMR",
  "THA",
  "VNM",
  "IDN",
  "MYS",
  "PHL",
  "TWN",
]);
const OPERATIONAL_NOISE_RE =
  /\b(payment|pymnt|autopay|automatic|electronic|transaction|thank|subscription|credit|debit|ach|purch|ref|reference|number|num|trace|confirm|confirmation|id|card)\b/g;

// ─── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Strips banking noise and corporate suffixes from a raw text string.
 * Returns the cleaned lowercase key, or an empty string if nothing useful remains.
 */
function cleanText(raw: string): string {
  let s = raw.trim().toLowerCase();

  // Normalize possessives without introducing an isolated "s" token.
  // "mcdonald's" -> "mcdonalds", "mcdonald s" -> "mcdonalds"
  s = s.replace(/'/g, "");
  s = s.replace(/\b([a-z0-9]+)\s+s\b/g, "$1s");

  // Remove wire transfer noise tokens (Srf# Trn# Rfb# Seq# Fed#)
  s = s.replace(/\b(srf|trn|rfb|seq|fed)#\S*/gi, "");
  s = s.replace(/\bwt\s+fed#\S*/gi, "");

  // Remove ACH/wire format codes
  s = s.replace(/\b(ach|eft|pos|ppd|ccd|web|tel)\b/g, "");

  // Remove embedded dates: MM/DD/YYYY, MM/DD/YY, or MM/DD
  s = s.replace(/\d{2}\/\d{2}\/\d{2,4}/g, "");
  s = s.replace(/\b\d{2}\/\d{2}\b/g, "");

  // Remove standalone numeric sequences ≥ 4 digits (trace IDs, phone, ref numbers)
  s = s.replace(/\b\d{4,}\b/g, "");

  // Remove short alpha + numeric confirmation codes like A0190, A5188, ST-4S3
  s = s.replace(/\b[a-z]{1,3}\d{3,}\b/gi, "");
  s = s.replace(/\b[a-z]{1,3}-[a-z0-9]{3,}\b/gi, "");

  // Remove alphanumeric mixed tokens ≥ 6 chars (Zelle IDs, trace tokens)
  s = s.replace(/\b(?=[a-z0-9]*[0-9])(?=[a-z0-9]*[a-z])[a-z0-9]{6,}\b/g, "");

  // Remove low-signal operational words when possible.
  const withoutOperationalWords = s.replace(OPERATIONAL_NOISE_RE, "");
  const operationalProbe = withoutOperationalWords
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (operationalProbe.length > 0) s = withoutOperationalWords;

  // Remove corporate suffixes
  s = s.replace(
    /\b(llc|inc|corp|co|ltd|plc|n\.a\.|na|s\.a\.|sas|s\.a\.c\.|nte\*zzz|gmbh|gmb)\b\.?/g,
    "",
  );

  // Remove leading numeric prefix like "1/" (wire transfer artifacts: "1/G E M Apoyo...")
  s = s.replace(/^\d+\//, "");

  // Strip domain suffixes left behind by URL noise (.com .io .net .co .org etc.)
  s = s.replace(/\.(com|io|net|co|org|app|dev|ai)\b/g, "");

  // Remove trailing punctuation (commas, dots, asterisks, hyphens)
  s = s.replace(/[,.*\-]+$/, "");

  // Strip WF-style *PRODUCT tokens (e.g. "ADOBE*ADOBE" → "ADOBE", "TMOBILE*POSTPAID" → "TMOBILE")
  // In WF card statements, asterisk always separates merchant from product/plan — never semantic.
  s = s.replace(/\*\S*/g, "");

  // Collapse whitespace
  s = s.replace(/\s+/g, " ").trim();

  // Dedup consecutive identical words that arise from merchant-as-domain or *PRODUCT repetition
  // e.g. "adobe adobe" → "adobe", "streamyard streamyard" → "streamyard"
  s = s
    .split(" ")
    .filter((w, i, arr) => w !== arr[i - 1])
    .join(" ");

  return s;
}

/** Converts a string to Title Case. */
function toTitleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeNonMerchantComparable(value: string): string {
  return value
    .toLowerCase()
    .replace(/https?:\/\/\S*/g, " ")
    .replace(/[•*]+/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\b\d{2,}\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const BANK_PRODUCT_WORDS_RE =
  /(?:io\s+)?(?:cash\s*back|cashback|checking|savings|credit|autopay|card|account|rewards?|statement\s+credit)/;
const BANK_PRODUCT_PREFIX_NOISE = new Set([
  "io",
  "cash",
  "back",
  "cashback",
  "credit",
  "debit",
  "deposit",
  "reward",
  "rewards",
  "statement",
  "autopay",
  "auto",
  "payment",
  "payments",
  "pmt",
  "pymt",
  "online",
  "bill",
  "fee",
  "transfer",
]);

function extractIssuerFromBankProductLabel(value: string | null): string | null {
  if (!value) return null;
  let key = normalizeNonMerchantComparable(value);
  if (!key) return null;

  const inlineParts = key.split(/\s+merchant\s+name\s+/);
  if (inlineParts.length > 1) {
    const prefix = inlineParts.slice(0, -1).join(" ");
    const prefixTokens = prefix.split(/\s+/).filter(Boolean);
    const prefixIsOperational =
      prefixTokens.length === 0 ||
      prefixTokens.every((token) => BANK_PRODUCT_PREFIX_NOISE.has(token));
    if (!prefixIsOperational) return null;
    key = inlineParts[inlineParts.length - 1].trim();
  }

  key = key
    .replace(/\s+(?:ending|acct|account|card)?\s*\d{2,}$/i, "")
    .replace(/\s+\d{2,}$/i, "")
    .trim();

  const match = key.match(new RegExp(`^(.+?)\\s+${BANK_PRODUCT_WORDS_RE.source}$`, "i"));
  if (!match) return null;

  const issuer = match[1]
    .replace(/\b(?:business|personal|commercial|corporate)\s*$/i, "")
    .trim();
  if (!issuer) return null;

  const issuerTokens = issuer.split(/\s+/).filter(Boolean);
  if (
    issuerTokens.length === 0 ||
    issuerTokens.every((token) => BANK_PRODUCT_PREFIX_NOISE.has(token)) ||
    issuerTokens.every((token) => token.length <= 2)
  ) {
    return null;
  }

  return issuer;
}

function isStandaloneNonMerchantDescriptor(value: string | null): boolean {
  if (!value) return false;
  const key = normalizeNonMerchantComparable(value);
  if (!key) return false;
  if (extractIssuerFromBankProductLabel(value)) return false;
  if (new RegExp(`^${BANK_PRODUCT_WORDS_RE.source}$`, "i").test(key)) return true;
  if (
    /^(?:credit|debit|statement)?\s*(?:cashback|cash back|reward|rewards)\s+(?:deposit|credit|reward|rewards)$/.test(key) ||
    /^(?:io\s+)?(?:cashback|cash back|autopay)$/.test(key)
  ) {
    return true;
  }
  return false;
}

function isDefinitiveNonMerchantDescriptor(fields: Array<string | null | undefined>): boolean {
  const populated = fields.filter((field): field is string => Boolean(field?.trim()));
  return populated.length > 0 && populated.every((field) => isStandaloneNonMerchantDescriptor(field));
}

function canonicalizeKnownMerchantKey(key: string): string {
  const bankProductIssuer = extractIssuerFromBankProductLabel(key);
  if (bankProductIssuer) {
    return bankProductIssuer;
  }
  if (isStandaloneNonMerchantDescriptor(key)) {
    return '';
  }
  if (
    /^mercury\s+technologies$/.test(key) ||
    /^mercury\s+io$/.test(key)
  ) {
    return 'mercury';
  }
  if (/^bold(?:\s+.*)?$/.test(key)) {
    return '';
  }
  if (/\bapple(?:[\s/]+com)?[\s/]+bill\b/.test(key) || /^apple$/.test(key)) {
    return 'apple';
  }
  if (/\bfigma\b/.test(key)) {
    return 'figma';
  }
  return key;
}

/**
 * Extracts the company name from ACH ORIG CO NAME patterns.
 *
 * e.g. "ORIG CO NAME:Square Inc ORIG ID:9424300002 DESC DATE:260401..." → "Square Inc"
 */
export function parseOrigCoName(text: string): string | null {
  const match = text.match(
    /ORIG\s+CO\s+NAME\s*:?\s*(.+?)(?:\s+ORIG\s+ID\b|\s+CO\s+ENTRY|\s+SEC\s+CODE|\s+TRACE\b|\s+DESC\s+DATE|\s+ORIG\s+CO\s+ID|\s{2,}|$)/i,
  );
  if (!match) return null;
  const captured = match[1].trim();
  if (captured.length === 0 || captured.length > 80) return null;
  return captured;
}

/**
 * Extracts merchant name from Wells Fargo / credit card debit descriptions.
 *
 * WF format: "[RECURRING PAYMENT|PURCHASE] [INTL] AUTHORIZED ON MM/DD MERCHANT [CITY] STATE SAUTH CARD XXXX"
 *
 * Strategy:
 * 1. Regex captures merchant+city and location code (state/country) SEPARATELY.
 *    The location code sits right before the auth code ([SP]\d{9+}).
 * 2. Validate the code against US_STATE_CODES (50 states + DC + territories) or
 *    ISO3_COUNTRY_CODES (common 3-letter codes). This is deterministic — not a heuristic.
 *    If unrecognized, fall back to the full-segment capture (no city stripping).
 * 3. Strip trailing city words from the raw merchant+city segment:
 *    - If the segment contains a special char (*, /, .) that anchors the merchant name,
 *      allow up to 2 trailing pure-alpha words to be stripped (handles multi-word cities).
 *    - Otherwise strip at most 1 trailing pure-alpha word (conservative).
 *    - Never strip to empty or below 2 chars.
 */
function parseCardStatementName(text: string): string | null {
  // Primary: capture merchant+city and state/country code separately.
  // Lazy (.+?) finds the FIRST split where ([A-Z]{2,3}) is immediately followed by the auth code.
  const match = text.match(
    /^(?:RECURRING\s+PAYMENT\s+(?:INTL\s+)?|PURCHASE\s+(?:INTL\s+)?)AUTHORIZED\s+ON\s+\d{2}\/\d{2}\s+(.+?)\s+([A-Z]{2,3})\s+[SP]\d{9,}/i,
  );
  if (!match) return null;

  const rawSegment = match[1].trim();
  const domainMatch = rawSegment.match(/\bwww\.?([a-z0-9-]{4,})\b/i);
  const firstWord = rawSegment.split(/\s+/)[0]?.replace(/[^a-z0-9]/gi, "");
  if (
    domainMatch?.[1] &&
    firstWord &&
    domainMatch[1].length > firstWord.length
  ) {
    return domainMatch[1];
  }

  let segment = rawSegment
    .replace(/\bhttps?\S*.*$/i, "")
    .replace(/\bwww\S*.*$/i, "")
    .trim();
  const locationCode = match[2].toUpperCase();

  // Validate: only strip city when we know the last token is truly a state/country code.
  // If the code is unknown (e.g. a merchant abbreviation), fall back to full segment (no city strip).
  const knownLocation = US_STATE_CODES.has(locationCode) || ISO3_COUNTRY_CODES.has(locationCode);
  if (!knownLocation) {
    // Unknown code — use the full capture including the matched "state" token, just before auth.
    const fallback = text.match(
      /^(?:RECURRING\s+PAYMENT\s+(?:INTL\s+)?|PURCHASE\s+(?:INTL\s+)?)AUTHORIZED\s+ON\s+\d{2}\/\d{2}\s+(.+?)\s+[SP]\d{9,}/i,
    );
    if (!fallback) return null;
    segment = fallback[1].trim();
    if (segment.length < 2 || segment.length > 100) return null;
    return segment;
  }

  // Strip trailing city words from the raw merchant+city segment.
  // A "city word" is a purely alphabetic token at the end of the segment.
  // We allow more stripping when the segment has a special char (., *, /) that
  // serves as a structural anchor for the merchant name (e.g. "ADOBE *ADOBE SAN JOSE").
  const hasSpecialChar = /[^A-Za-z0-9\s]/.test(segment);
  const maxCityWords = hasSpecialChar ? 2 : 1;

  if (rawSegment.includes("*")) {
    const [left, ...rightParts] = rawSegment.split("*");
    const right = rightParts.join("*").trim();
    if (/^(?:tst|toast|sq|square)$/i.test(left.trim()) && right) {
      segment = right
        .replace(/\s+-\s+.+$/i, "")
        .replace(/\bhttps?\S*.*$/i, "")
        .replace(/\bwww\S*.*$/i, "")
        .replace(/\s+/g, " ")
        .trim();
      const words = segment.split(" ");
      if (words.length >= 3 && /^[A-Za-z]+$/.test(words[words.length - 1])) {
        segment = words.slice(0, -1).join(" ").trim();
      }
      if (segment.length < 2 || segment.length > 100) return null;
      return segment;
    }
  }

  const words = segment.split(/\s+/);
  let end = words.length;
  let stripped = 0;
  while (stripped < maxCityWords && end > 1) {
    if (/^[A-Za-z]+$/.test(words[end - 1])) {
      end--;
      stripped++;
    } else {
      break; // token has special char — it's part of the merchant, stop here
    }
  }

  const result = words.slice(0, end).join(" ");
  if (result.length < 2 || result.length > 100) return null;
  return result;
}

/**
 * Extracts merchant name from Plaid's inline annotation pattern:
 *   "; Merchant Name: XXX" embedded in the name field.
 *
 * e.g. "Io Autopay; Merchant Name: Mercury Credit"     → "Mercury"
 * e.g. "Claude.Ai Subscription; Merchant Name: Anthropic" → "Anthropic"
 * e.g. "Streamyard.Com; Merchant Name: Streamyard"    → "Streamyard"
 */
function parseInlineMerchantName(text: string): string | null {
  // For incoming wire descriptors, inline "Merchant Name" usually echoes the
  // beneficiary account holder (often own entity), not an external counterparty.
  if (/^\s*incoming\s+wire\b/i.test(text) || /(?:\/Bnf=|\/Org=|Bnf=|Org=)/i.test(text)) {
    return null;
  }
  const match = text.match(/;\s*Merchant\s+Name:\s*(.+?)(?:;|$)/i);
  if (!match) return null;
  const captured = match[1].trim();
  if (captured.length === 0 || captured.length > 100) return null;
  return captured;
}

function parseStripeTransfer(text: string): string | null {
  return /\bSTRIPE\b/i.test(text) ? "Stripe" : null;
}

function parseBold(text: string): string | null {
  const match = text.match(/\bbold\s+(.+)$/i);
  if (!match) return null;
  let captured = match[1]
    .replace(/\s+/g, " ")
    .replace(/^(?:co|colombia)\s+/i, "")
    .trim();
  const beforeLocationStrip = captured;
  captured = captured
    .replace(
      /\s+(?:bogot[aá](?:\s+d\.?\s*c\.?)?|medell[ií]n|cali|barranquilla|cartagena|rionegro|envigado|pereira|manizales|bucaramanga|colombia|col)(?:\s+.*)?$/i,
      "",
    )
    .trim();
  if (captured !== beforeLocationStrip) {
    captured = captured.replace(/\s+(?:e|y|and)\s*$/i, "").trim();
  }
  if (captured.length < 2 || captured.length > 80) return null;
  return captured;
}

function parseDeelPayout(text: string): string | null {
  return /^\s*'?Deel[, ]/i.test(text) ? "Deel" : null;
}

function parseZellePartyName(text: string): string | null {
  const match = text.match(/\bZELLE\s+(?:FROM|TO)\s+(.+?)(?:\s+ON\s+\d{2}\/\d{2}|\s+REF\s*#|$)/i);
  if (!match) return null;
  const captured = match[1].trim();
  if (captured.length < 2 || captured.length > 120) return null;
  return captured;
}

function parseAchPayee(text: string): string | null {
  if (!/\bach\b/i.test(text)) return null;

  const withCreditDebit = text.match(
    /\bACH\s+ELECTRONIC\s+(?:CREDIT|DEBIT)\s*([A-Z][A-Z0-9&.\- ]{1,80}?)(?:\s+(?:PAY|PMT|PYMT|PAYROLL|DEP(?:OSIT)?)\b|\s+\d{3,}|$)/i,
  );
  const candidate = withCreditDebit?.[1]?.trim();
  if (candidate && candidate.length >= 2) return candidate;

  const generic = text.match(
    /\bACH\s+(?:CREDIT|DEBIT)\s*([A-Z][A-Z0-9&.\- ]{1,80}?)(?:\s+(?:PAY|PMT|PYMT|PAYROLL|DEP(?:OSIT)?)\b|\s+\d{3,}|$)/i,
  );
  const fallback = generic?.[1]?.trim();
  if (fallback && fallback.length >= 2) return fallback;

  return null;
}

/**
 * Extracts the beneficiary/originator from wire transfer descriptions.
 *
 * e.g. "Wt - Banco Davivienda S Bnf=Top Job Empresa De Servicios Srf# Trn#" → "Top Job Empresa De Servicios"
 * e.g. "Wt Bbva Mexico /Org=G E M Apoyo A Empresa Joven Srf#..."            → "G E M Apoyo A Empresa Joven"
 */
function parseWireParty(text: string): string | null {
  const match = text.match(
    /(?:\/Bnf=|Bnf=|\/Org=|Org=)(.+?)(?:\s+Srf#|\s+Trn#|\s+Rfb#|\s+Fed#|$)/i,
  );
  if (!match) return null;
  // Strip leading numeric prefix like "1/" (wire format artifact: "/Org=1/G E M ...")
  let captured = match[1].trim().replace(/^\d+\//, "");
  if (captured.length < 3 || captured.length > 100) return null;
  return captured;
}

/**
 * Extracts the card issuer name from card payment descriptions.
 * Card payments often append payment keywords and the cardholder's name:
 *
 * e.g. "Capital One Crcardpmt Diego Rodriguez"     → "Capital One"
 * e.g. "Amex Epayment Pmt A0190 Diego Rodriguez"   → "Amex"
 * e.g. "Discover Bank Prearrange Diegorodriguez"   → "Discover Bank"
 * e.g. "Discover Net/Mobile Diegorodriguez"        → "Discover"
 * e.g. "Credit One Bank Diego Rodriguez"           → "Credit One Bank"
 */
function parseCardPayment(text: string): string | null {
  if (/\bcredit\s+one\s+bank\b/i.test(text)) return "Credit One Bank";
  if (/\bdiscover\s+(?:bank|e-?payment|epayment|prearrange|net\/mobile|net|mobile|retry|card)\b/i.test(text)) {
    return "Discover";
  }

  const match = text.match(
    /^(.+?)\s+(?:crcardpmt|epayment|e-payment|prearrange|net\/mobile|mobile\s*pmt|retry\s*pymt|e-pmt|e-\b|onlinepymt|bank\s+prearrange|bank\s+retry)\b/i,
  );
  if (!match) return null;
  const issuer = match[1].trim();
  if (issuer.length < 2 || issuer.length > 60) return null;
  return issuer;
}

/**
 * Normalizes well-known recurring fee descriptions into canonical names.
 * Returns null if no fee pattern matches.
 */
function parseFeeName(text: string): string | null {
  const t = text.toLowerCase();
  if (/^international purch trans fee reversal/.test(t))
    return "International Transaction Fee Reversal";
  if (
    /^(international purchase transaction fee|international purch trans fee|intl\.\s*fee|intl\.?\s+trans(?:action)?\s+fee|our fee for international wire)/.test(
      t,
    )
  )
    return "International Transaction Fee";
  if (/^wire\s+trans\s+svc\s+charge/.test(t)) return "Wire Transfer Fee";
  if (/^overdraft fee/.test(t)) return "Overdraft Fee";
  if (/^monthly service fee/.test(t)) return "Monthly Service Fee";
  if (/^(non-wells fargo atm fee|non-wf atm)/.test(t)) return "ATM Fee";
  if (/^(international purch trans fee reversal|intl.*reversal)/.test(t))
    return "International Transaction Fee Reversal";
  return null;
}

/**
 * Picks the best Zelle counterparty name from the counterparties array.
 */
function pickZelleCounterparty(tx: PlaidTransaction): string | null {
  const counterparties = tx.counterparties as Array<{
    name?: string;
    type?: string;
    confidence_level?: string;
  }> | null;

  if (!counterparties || counterparties.length === 0) return null;

  const CONFIDENCE_ORDER = ["VERY_HIGH", "HIGH", "MEDIUM", "LOW", "UNKNOWN"];
  const sorted = [...counterparties].sort((a, b) => {
    const ai = CONFIDENCE_ORDER.indexOf(a.confidence_level ?? "UNKNOWN");
    const bi = CONFIDENCE_ORDER.indexOf(b.confidence_level ?? "UNKNOWN");
    return ai - bi;
  });

  return sorted[0]?.name?.trim() || null;
}

/** Build a NormalizedMerchant from a raw name string and source. */
function make(raw: string, source: NormalizationSource): NormalizedMerchant | null {
  if (/^\s*credit\s+one\s+bank\s*$/i.test(raw)) {
    return { key: "credit one bank", display: "Credit One Bank", source };
  }
  if (/^\s*discover(?:\s+bank)?\s*$/i.test(raw)) {
    return { key: "discover", display: "Discover", source };
  }
  const key = canonicalizeKnownMerchantKey(cleanText(raw));
  if (!key) return null;
  return { key, display: toTitleCase(key), source };
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Derives a normalized merchant identity from a PlaidTransaction.
 *
 * Priority:
 * 0. merchantEntityId  — most stable Plaid identifier; used as key when present
 * 1. ORIG CO NAME      — ACH transfers (name or originalDescription)
 * 2. Inline annotation — "; Merchant Name: XXX" embedded in name field
 * 3. Zelle counterparty
 * 4. Card payment      — extracts issuer from "Capital One Crcardpmt ..." patterns
 * 5. Wire party        — extracts /Org= or /Bnf= from WT descriptions
 * 6. Fee patterns      — collapses recurring fee descriptions to canonical names
 * 7. merchantName      — Plaid-provided (cleaned)
 * 8. name              — raw name field (cleaned)
 * 9. originalDescription
 */
export function normalizeMerchant(tx: PlaidTransaction): NormalizedMerchant {
  const name = tx.name || "";
  const desc = tx.originalDescription || "";

  // 0. merchantEntityId — use as key but derive display name from the normal pipeline
  //    We process normally for display, then override the key at the end.
  const entityId = tx.merchantEntityId ?? null;

  if (isDefinitiveNonMerchantDescriptor([tx.merchantName, name, desc])) {
    return { key: entityId ?? "unknown", display: "Unknown", source: "NAME" };
  }

  // Helper: return result, optionally overriding key with entityId
  const ret = (r: NormalizedMerchant | null): NormalizedMerchant | null =>
    r && entityId ? { ...r, key: entityId } : r;

  // 1. Fee pattern normalization — runs first so canonical names win over inline annotations
  //    e.g. "OUR fee for international wire; Merchant name: Intl. Wire Fee" → "International Transaction Fee"
  const feeName = parseFeeName(name) || (desc ? parseFeeName(desc) : null);
  if (feeName) {
    const key = feeName.toLowerCase();
    const r: NormalizedMerchant = { key, display: feeName, source: "NAME" };
    return ret(r) ?? r;
  }

  // 2. Known settlement rails with high-entropy payloads
  //    Keep canonical merchant identity stable regardless of per-transfer IDs.
  const deel = parseDeelPayout(name) || (desc ? parseDeelPayout(desc) : null);
  if (deel) {
    const r = ret(make(deel, "NAME"));
    if (r) return r;
  }

  const stripe = parseStripeTransfer(name) || (desc ? parseStripeTransfer(desc) : null);
  if (stripe) {
    const r = ret(make(stripe, "NAME"));
    if (r) return r;
  }

  const bold = parseBold(name) || (desc ? parseBold(desc) : null);
  if (bold) {
    const r = ret(make(bold, 'NAME'));
    if (r) return r;
  }

  // 2. ORIG CO NAME
  const achPayee = parseAchPayee(name) || (desc ? parseAchPayee(desc) : null);
  if (achPayee) {
    const r = ret(make(achPayee, "ACH_PAYEE"));
    if (r) return r;
  }

  // 3. ORIG CO NAME
  const origFromName = parseOrigCoName(name);
  if (origFromName) {
    const r = ret(make(origFromName, "ORIG_CO_NAME"));
    if (r) return r;
  }
  if (desc) {
    const origFromDesc = parseOrigCoName(desc);
    if (origFromDesc) {
      const r = ret(make(origFromDesc, "ORIG_CO_NAME"));
      if (r) return r;
    }
  }

  // 4. Inline "; Merchant Name: XXX"
  const inlineName = parseInlineMerchantName(name) || (desc ? parseInlineMerchantName(desc) : null);
  if (inlineName) {
    // Also apply fee normalization to the inline result
    const inlineFee = parseFeeName(inlineName);
    if (inlineFee) {
      const r: NormalizedMerchant = {
        key: inlineFee.toLowerCase(),
        display: inlineFee,
        source: "NAME",
      };
      return ret(r) ?? r;
    }
    const r = ret(make(inlineName, "INLINE_MERCHANT"));
    if (r) return r;
  }

  // 5. Zelle counterparty
  if (isZelle(tx)) {
    const cp = pickZelleCounterparty(tx);
    if (cp) {
      const r = ret(make(cp, "ZELLE_COUNTERPARTY"));
      if (r) return r;
    }
    const parsedCp = parseZellePartyName(name) || (desc ? parseZellePartyName(desc) : null);
    if (parsedCp) {
      const r = ret(make(parsedCp, "ZELLE_COUNTERPARTY"));
      if (r) return r;
    }
  }

  // 6. Card statement description ("RECURRING PAYMENT AUTHORIZED ON MM/DD MERCHANT...")
  const cardStmt = parseCardStatementName(name) || (desc ? parseCardStatementName(desc) : null);
  if (cardStmt) {
    const raw = make(cardStmt, "CARD_PAYMENT");
    if (raw) {
      // After cleanText (which strips domain suffixes like .com/.io), the same merchant
      // name may still appear twice as consecutive tokens due to the WF pattern
      // "MERCHANT MERCHANT.DOMAIN" → after stripping → "merchant merchant".
      // Dedup consecutive identical words to collapse this invariant.
      const deduped = raw.key
        .split(" ")
        .filter((w, i, arr) => w !== arr[i - 1])
        .join(" ")
        .trim();
      const r = deduped
        ? ret({ key: deduped, display: toTitleCase(deduped), source: "CARD_PAYMENT" })
        : null;
      if (r) return r;
    }
  }

  // 7. Card payment issuer extraction ("Capital One Crcardpmt Diego Rodriguez")
  const cardIssuer = parseCardPayment(name) || (desc ? parseCardPayment(desc) : null);
  if (cardIssuer) {
    const r = ret(make(cardIssuer, "CARD_PAYMENT"));
    if (r) return r;
  }

  // 8. Wire transfer /Org= or /Bnf= extraction
  const wireParty = parseWireParty(name) || (desc ? parseWireParty(desc) : null);
  if (wireParty) {
    const r = ret(make(wireParty, "WIRE_PARTY"));
    if (r) return r;
  }

  // 9. merchantName (Plaid-provided)
  if (tx.merchantName) {
    const r = ret(make(tx.merchantName, "MERCHANT_NAME"));
    if (r) return r;
  }

  // 10. name field (cleaned)
  if (name) {
    const r = ret(make(name, "NAME"));
    if (r) return r;
  }

  // 11. originalDescription (last resort)
  if (desc) {
    const r = ret(make(desc, "DESCRIPTION"));
    if (r) return r;
  }

  return { key: entityId ?? "unknown", display: "Unknown", source: "NAME" };
}

// ─── Key canonicalization (proximity dedup) ────────────────────────────────────

/**
 * Returns true when a token list is "specific enough" to drive a merge.
 * Prevents short noise tokens ("co", "the", "de") from creating false positives.
 *   - At least two tokens of length ≥ 3, OR
 *   - At least one token of length ≥ 5
 */
function isSpecificEnough(tokenList: string[]): boolean {
  const meaningful = tokenList.filter((t) => t.length >= 3);
  return meaningful.length >= 2 || meaningful.some((t) => t.length >= 5);
}

/**
 * Given a collection of normalized merchant keys, returns a Map<originalKey, canonicalKey>
 * that collapses keys representing the same merchant.
 *
 * Two keys are considered the same merchant when all tokens of the shorter key
 * are contained in the longer key (word-set subset containment). The canonical
 * representative is the shortest key in each connected component (fewest tokens,
 * lexicographic tiebreak).
 *
 * Example:
 *   ["adobe", "adobe creative cloud", "spotify", "spotify ab"]
 *   → "adobe creative cloud" → "adobe"
 *   → "spotify ab"          → "spotify"
 *
 * Complexity: O(n²) on unique keys — fine for typical grouping sizes (< 1000 keys).
 */
export function canonicalizeKeys(keys: Iterable<string>): Map<string, string> {
  const unique = Array.from(new Set(keys));

  // Tokenize: split by space, keep tokens with length ≥ 2
  const tokenMap = new Map<string, string[]>();
  for (const k of unique) {
    tokenMap.set(
      k,
      k.split(" ").filter((t) => t.length >= 2),
    );
  }

  // Union-Find
  const parent = new Map<string, string>(unique.map((k) => [k, k]));
  function find(k: string): string {
    if (parent.get(k) !== k) parent.set(k, find(parent.get(k)!));
    return parent.get(k)!;
  }
  function union(a: string, b: string): void {
    const ra = find(a),
      rb = find(b);
    if (ra === rb) return;
    // Canonical = fewest tokens; tiebreak: lexicographic (shorter/simpler string)
    const aLen = tokenMap.get(ra)!.length;
    const bLen = tokenMap.get(rb)!.length;
    if (aLen < bLen || (aLen === bLen && ra < rb)) {
      parent.set(rb, ra);
    } else {
      parent.set(ra, rb);
    }
  }

  // Compare every pair: if the shorter key's tokens are all present in the longer key → same merchant
  for (let i = 0; i < unique.length; i++) {
    for (let j = i + 1; j < unique.length; j++) {
      const a = unique[i],
        b = unique[j];
      const aT = tokenMap.get(a)!;
      const bT = tokenMap.get(b)!;
      const [shorterT, longerT] = aT.length <= bT.length ? [aT, bT] : [bT, aT];

      if (!isSpecificEnough(shorterT)) continue;

      const isSubset = shorterT.every((t) => longerT.includes(t));
      if (isSubset) union(a, b);
    }
  }

  // Resolve all roots
  const result = new Map<string, string>();
  for (const k of unique) {
    result.set(k, find(k));
  }
  return result;
}

/**
 * Derives the semantic payment type from a transaction.
 *
 * Priority: CHECK > ZELLE > transactionCode mapping > OTHER
 * transactionCode === null → 'OTHER' (conservative, no over-classification)
 */
export function deriveSemanticType(tx: PlaidTransaction): SemanticType {
  if (isCheck(tx)) return "CHECK";
  if (isZelle(tx)) return "ZELLE";

  const code = tx.transactionCode?.toLowerCase() ?? null;
  if (code === null) return "OTHER";
  if (code === "transfer") return "TRANSFER";
  if (code === "atm") return "ATM";
  if (DIRECT_DEBIT_CODES.has(code)) return "DIRECT_DEBIT";
  if (code === "purchase") return "PURCHASE";

  return "OTHER";
}

/**
 * Returns true only if the transaction is an explicit self-transfer between
 * own accounts, based on Plaid PFC detailed category.
 */
export function deriveIsSelf(tx: PlaidTransaction): boolean {
  if (!tx.personalFinanceCategoryDetailed) return false;
  return SELF_TRANSFER_PFC_DETAILED.has(tx.personalFinanceCategoryDetailed);
}
